import { useState, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import {
  CheckCircle,
  XCircle,
  Building,
  User,
  Briefcase,
  AlertCircle,
  Loader2,
  MessageSquare,
} from 'lucide-react'
import { Button } from '@/modules/shared/ui/components/button'
import { Textarea } from '@/modules/shared/ui/components/textarea'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/modules/shared/ui/components/card'
import { Badge } from '@/modules/shared/ui/components/badge'
import { toast } from 'sonner'
import { useRecaptcha } from '@/modules/shared/hooks/use-recaptcha'
import {
  useVacancySuggestionToken,
  useRespondToVacancySuggestion,
} from '../hooks/vacancySuggestionHook'

const MAX_NOTE_LENGTH = 2000
const RECAPTCHA_ACTION = 'vacancy_suggestion_respond'

export default function VacancySuggestionResponsePage() {
  const { token } = useParams<{ token: string }>()
  const [action, setAction] = useState<'accept' | 'reject' | null>(null)
  const [note, setNote] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [outcome, setOutcome] = useState<{ outcome: string; message: string } | null>(null)

  const { data: tokenData, isLoading, error } = useVacancySuggestionToken(token)
  const respondMutation = useRespondToVacancySuggestion()
  const { getToken: getCaptchaToken } = useRecaptcha()

  const handleAccept = useCallback(async () => {
    if (!token) return
    setIsSubmitting(true)
    try {
      const captchaToken = await getCaptchaToken(RECAPTCHA_ACTION)
      const result = await respondMutation.mutateAsync({
        token,
        newStatus: 'Accepted',
        note: note.trim() || null,
        captchaToken,
      })
      setOutcome(result)
      setAction(null)
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to submit response. Please try again.'
      toast.error(message)
    } finally {
      setIsSubmitting(false)
    }
  }, [token, note, respondMutation, getCaptchaToken])

  const handleReject = useCallback(async () => {
    if (!token) return
    setIsSubmitting(true)
    try {
      const captchaToken = await getCaptchaToken(RECAPTCHA_ACTION)
      const result = await respondMutation.mutateAsync({
        token,
        newStatus: 'Rejected',
        note: note.trim() || null,
        captchaToken,
      })
      setOutcome(result)
      setAction(null)
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to submit response. Please try again.'
      toast.error(message)
    } finally {
      setIsSubmitting(false)
    }
  }, [token, note, respondMutation, getCaptchaToken])

  if (!token) {
    return (
      <section className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-foreground mb-2">Invalid Link</h2>
          <p className="text-muted-foreground">This suggestion link is missing or invalid.</p>
        </div>
      </section>
    )
  }

  if (isLoading) {
    return (
      <section className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#1b237a] mx-auto mb-4" />
          <p className="text-lg text-muted-foreground">Loading suggestion details…</p>
        </div>
      </section>
    )
  }

  if (error || !tokenData) {
    return (
      <section className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="text-center max-w-md">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-foreground mb-2">Unable to load suggestion</h2>
          <p className="text-muted-foreground">
            The suggestion link may be expired or invalid. Please contact the recruiting team.
          </p>
        </div>
      </section>
    )
  }

  if (!tokenData.isValid) {
    if (tokenData.isUsed) {
      return (
        <section className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
          <div className="text-center max-w-md py-16">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-foreground mb-2">Already responded</h2>
            <p className="text-muted-foreground">
              You have already responded to this suggestion. Thank you!
            </p>
          </div>
        </section>
      )
    }
    if (tokenData.isExpired) {
      return (
        <section className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
          <div className="text-center max-w-md py-16">
            <AlertCircle className="w-16 h-16 text-orange-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-foreground mb-2">Link expired</h2>
            <p className="text-muted-foreground">
              This suggestion link has expired. Please contact the recruiting team if you are still
              interested.
            </p>
          </div>
        </section>
      )
    }
    return (
      <section className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="text-center max-w-md py-16">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-foreground mb-2">Invalid link</h2>
          <p className="text-muted-foreground">
            This suggestion link is not valid. Please contact the recruiting team.
          </p>
        </div>
      </section>
    )
  }

  if (outcome) {
    const isAccepted = outcome.outcome === 'Accepted'
    return (
      <section className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="text-center max-w-md py-16">
          {isAccepted ? (
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          ) : (
            <XCircle className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          )}
          <h2 className="text-2xl font-bold text-foreground mb-2">
            {isAccepted ? "You're in!" : 'Response recorded'}
          </h2>
          <p className="text-muted-foreground">{outcome.message}</p>
        </div>
      </section>
    )
  }

  const data = tokenData

  return (
    <section className="min-h-screen py-12 sm:py-16 bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-[#1b237a] to-[#1a2a8f] bg-clip-text text-transparent mb-3">
              New opportunity for you
            </h1>
            {data.candidateFirstName && (
              <p className="text-lg text-muted-foreground">
                Hi {data.candidateFirstName}, someone thinks you could be a great fit for a new role.
              </p>
            )}
          </div>

          <Card className="mb-6 shadow-lg">
            <CardHeader>
              <div className="flex items-start justify-between gap-4">
                <CardTitle className="text-xl font-semibold text-foreground">
                  {data.targetVacancyTitle}
                </CardTitle>
                {data.targetVacancyDepartment && (
                  <Badge variant="outline">{data.targetVacancyDepartment}</Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {data.organizationName && (
                  <div className="flex items-center gap-3">
                    <Building className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                    <div>
                      <p className="text-xs text-muted-foreground">Organisation</p>
                      <p className="font-medium text-foreground">{data.organizationName}</p>
                    </div>
                  </div>
                )}
                {data.suggestedByName && (
                  <div className="flex items-center gap-3">
                    <User className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                    <div>
                      <p className="text-xs text-muted-foreground">Suggested by</p>
                      <p className="font-medium text-foreground">{data.suggestedByName}</p>
                    </div>
                  </div>
                )}
              </div>

              {data.vacancySummary && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Briefcase className="w-4 h-4 text-muted-foreground" />
                    <p className="text-sm font-semibold text-foreground">About the role</p>
                  </div>
                  <p className="text-muted-foreground leading-relaxed text-sm bg-muted/40 rounded-lg p-3 border border-border">
                    {data.vacancySummary}
                  </p>
                </div>
              )}

              {data.salaryRange && (
                <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3">
                  <p className="text-sm font-medium text-green-800">
                    Salary range: <span className="font-semibold">{data.salaryRange}</span>
                  </p>
                </div>
              )}

              {data.recruiterMessage && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <MessageSquare className="w-4 h-4 text-muted-foreground" />
                    <p className="text-sm font-semibold text-foreground">Message from recruiter</p>
                  </div>
                  <blockquote className="border-l-4 border-primary/30 pl-4 text-sm text-muted-foreground italic leading-relaxed">
                    {data.recruiterMessage}
                  </blockquote>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-foreground">
                Are you interested?
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              {action === null ? (
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    onClick={() => setAction('accept')}
                    className="flex-1"
                    size="lg"
                  >
                    <CheckCircle className="w-5 h-5 mr-2" />
                    I&apos;m interested
                  </Button>
                  <Button
                    onClick={() => setAction('reject')}
                    variant="outline"
                    className="flex-1"
                    size="lg"
                  >
                    <XCircle className="w-5 h-5 mr-2" />
                    Not for me
                  </Button>
                </div>
              ) : action === 'accept' ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Message (optional)
                    </label>
                    <Textarea
                      value={note}
                      onChange={(e) => setNote(e.target.value.slice(0, MAX_NOTE_LENGTH))}
                      placeholder="Anything you'd like the team to know…"
                      className="min-h-[80px]"
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button
                      onClick={handleAccept}
                      disabled={isSubmitting}
                      className="flex-1"
                    >
                      {isSubmitting ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <CheckCircle className="w-4 h-4 mr-2" />
                      )}
                      Confirm — I&apos;m interested
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => { setAction(null); setNote('') }}
                      disabled={isSubmitting}
                    >
                      Back
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Reason <span className="text-muted-foreground font-normal">(optional)</span>
                    </label>
                    <Textarea
                      value={note}
                      onChange={(e) => setNote(e.target.value.slice(0, MAX_NOTE_LENGTH))}
                      placeholder="Let us know why this role isn't the right fit…"
                      className="min-h-[80px]"
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button
                      onClick={handleReject}
                      disabled={isSubmitting}
                      variant="outline"
                      className="flex-1"
                    >
                      {isSubmitting ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <XCircle className="w-4 h-4 mr-2" />
                      )}
                      Confirm — not for me
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => { setAction(null); setNote('') }}
                      disabled={isSubmitting}
                    >
                      Back
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  )
}
